import { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import useAuth from "../../hooks/useAuth";
import AxiosHelper from "../../api/AxiosHelper";
import { markActivity } from "../../utils/activityTracker";
import Wrapper from "./Wrapper";
import InputPassword from "../../components/form/InputPassword";
import { useTranslation } from "react-i18next";
import FailedLogin from "./FailedLogin";
import Constants from "../../utils/constants";

const Login = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { auth, setAuth, ready, setPanel, setToast } = useAuth();
    const usernameRef = useRef();
    
    const [username, setUsername] = useState(''); 
    const [password, setPassword] = useState('');

    const [failedCount, setFailedCount] = useState();
    const [countDown, setCountDown] = useState(null);

    const [mfaRequired, setMfaRequired] = useState(false);
    const [tempToken, setTempToken] = useState('');
    const [otpCode, setOtpCode] = useState('');

    useEffect(() => {
        if (ready && !auth.token) usernameRef.current.focus();
    }, [ready]);

    useEffect(() => {
        if (auth != 'Unauthorized' && ready)
            navigateToPage(auth.role);
    }, [auth]);

    const navigateToPage = (role) => {
        switch(role.type) {
            case Constants.ROOT:
            case Constants.ADMIN:
                setPanel('ADMIN');
                localStorage.setItem('panel', 'ADMIN');
                navigate("/");
                break;
            case Constants.STAFF:
                setPanel('STAFF');
                localStorage.setItem('panel', 'STAFF');
                navigate("/sp");
                break;
            default: navigate("/"); break;
        }
    }
    
    const handleLoginSubmit = (event) => {
        event.preventDefault();

        const data = { username, password }
        AxiosHelper.login(data).then((res) => {
            if (res.status === 200) {
                if (res.data.mfaRequired) {
                    setTempToken(res.data.tempToken);
                    setMfaRequired(true);
                    return;
                }
                const authData = res.data;
                markActivity();
                setAuth(authData);
                navigateToPage(authData.role);
            }
        }).catch((err) => {
            if (!err?.response) {
                setToast({ content: t('NO_SERVER_RESPONSE'), variant: 'danger', title: t('ERROR'), show: true });
                return;
            }
            switch(err.response.status) {
                case 402:
                    setToast({ content: t('VALIDATION_ERROR'), variant: 'danger', title: t('FAILED_LOGIN'), show: true });
                    setFailedCount(err.response.data.failedCount);
                    break;
                case 404: setToast({ content: t('USER_NOT_FOUND'), variant: 'warning', title: t('NOT_FOUND'), show: true }); break;
                case 410:
                    setToast({ content: t('PLEASE_WAIT_THEN_TRY_AGAIN'), variant: 'danger', title: t('ERROR'), show: true });
                    break;
                case 411:
                    setToast({ content: t('TOO_MANY_FAILED_LOGIN_ATTEMPTS'), variant: 'danger', title: t('ERROR'), show: true });
                    setFailedCount(err.response.data.failedCount);
                    const endTimestamp = new Date().getTime() + 60 * 1000;
                    localStorage.setItem('timerEnd', new Date(endTimestamp));
                    setCountDown(60);
                    break;
                case 420: setToast({ content: t('EMAIL_OR_PWD_NOT_ENTERED'), variant: 'warning', title: t('WARNING'), show: true }); break;
                case 403: setToast({ content: t('PERMISSION_RESTRICTED'), variant: 'danger', title: t('ERROR'), show: true }); break;
                default : setToast({ content: err.response.data.msg, variant: 'danger', title: t('ERROR'), show: true }); break;
            }
        });
    }

    const handleMfaVerify = (event) => {
        event.preventDefault();
        if (!otpCode) {
            setToast({ content: t('ERR_EMPTY_OTP_FIELD'), variant: 'warning', title: t('WARNING'), show: true });
            return;
        }
        AxiosHelper.mfaVerifyLogin({ tempToken, otpCode }).then((res) => {
            if (res.status === 200) {
                const authData = res.data;
                markActivity();
                setAuth(authData);
                navigateToPage(authData.role);
            }
        }).catch((err) => {
            if (!err?.response) {
                setToast({ content: t('NO_SERVER_RESPONSE'), variant: 'danger', title: t('ERROR'), show: true });
                return;
            }
            switch(err.response.status) {
                case 401: setToast({ content: t('INVALID_OTP'), variant: 'danger', title: t('ERROR'), show: true }); break;
                case 402: setToast({ content: t('MFA_TEMP_TOKEN_EXPIRED'), variant: 'danger', title: t('ERROR'), show: true }); break;
                case 403: setToast({ content: t('PERMISSION_RESTRICTED'), variant: 'danger', title: t('ERROR'), show: true }); break;
                default : setToast({ content: err.response.data.msg, variant: 'danger', title: t('ERROR'), show: true }); break;
            }
        });
    }

    if (mfaRequired) return (
        <Wrapper>
            <div className="d-flex flex-column">
                <div className="d-flex flex-column mx-5">
                    <h1 className="fw-bold display-5">{t('MFA_REQUIRED')}</h1>
                    <p className="text-muted mb-5">{t('MFA_REQUIRED_HINT')}</p>
                </div>
                <form onSubmit={handleMfaVerify} className="mx-5" noValidate>
                    <input
                        type="text"
                        autoFocus={true}
                        className="form-control mb-3"
                        placeholder={t('X_DIGIT_CODE')}
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        maxLength={6}
                        inputMode="numeric"
                    />
                    <div className="d-flex flex-row justify-content-between align-items-center">
                        <button type="button"
                            className="button-u fg-dark border-secondary font-14 py-2 rounded-2"
                            onClick={() => { setMfaRequired(false); setTempToken(''); setOtpCode(''); }}>
                            {t('BACK')}
                        </button>
                        <button type="submit"
                            className="button-u col-md-4 bg-blue-650 bg-gradient text-white font-14 border border-primary my-3 py-2 rounded-2"
                            disabled={otpCode.length !== 6}>
                            {t('SUBMIT')}
                        </button>
                    </div>
                </form>
            </div>
        </Wrapper>
    );

    return (
        (!ready && auth.token == 'Unauthorized') &&
        <Wrapper>
            <div className="d-flex flex-column">
                <div className="d-flex flex-column mx-5">
                    <h1 className="fw-bold display-5">{t('SIGN_IN')}</h1>
                    <p className="text-muted mb-5">{t('ENTER_LOGIN_CREDENTIALS')}</p>
                </div>
                <form onSubmit={handleLoginSubmit} className="mx-5" noValidate>
                    <FailedLogin
                        failedCount={failedCount}
                        setFailedCount={setFailedCount}
                        countDown={countDown}
                        setCountDown={setCountDown}
                    />
                    <input
                        type="text" ref={usernameRef} autoFocus={true}
                        className="form-control mb-3"
                        placeholder={t('USERNAME')}
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={countDown}
                    />
                    <InputPassword
                        className={'mt-3 mb-2'} inputClass={'py-2'}
                        value={password} setValue={setPassword}
                        hint={t('YOUR_PASSWORD')} required={true} disabled={countDown}
                    />
                    <div className="d-flex flex-row justify-content-between">
                        <span></span>
                        <button type="submit"
                            className="button-u col-md-4 bg-green-700 bg-gradient text-white font-14 border border-success my-3 py-2 rounded-2"
                            disabled={(!username || !password || countDown)}>
                            {t('SIGN_IN')}
                        </button>
                    </div>
                </form>
            </div>
        </Wrapper>
    );
};

export default Login;